import { useEffect, useState, type RefObject } from 'react'

export interface PrintInsets {
  top: number
  right: number
  bottom: number
  left: number
}
export const NO_PRINT_INSETS: PrintInsets = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
}

/** Reserve the actual floating panel widths instead of fitting the sheet behind them. */
export function printInsetsForPanels(
  container: DOMRect,
  panels: readonly DOMRect[],
): PrintInsets {
  let top = 0
  let bottom = 0
  let left = 0
  let right = 0
  const midX = (container.left + container.right) / 2
  const midY = (container.top + container.bottom) / 2
  for (const panel of panels) {
    if (
      panel.width <= 0 ||
      panel.height <= 0 ||
      panel.bottom <= container.top ||
      panel.top >= container.bottom
    )
      continue
    // A panel across the vertical midline is docked to the top or bottom edge
    // (the print panel on a phone). Reserving it as a side strip instead sized
    // that strip to almost the whole width, squeezing the frame into the 100px
    // floor below while the panel still covered it from underneath.
    if (panel.left < midX && panel.right > midX) {
      if ((panel.top + panel.bottom) / 2 < midY) {
        top = Math.max(top, panel.bottom - container.top + 12)
      } else {
        bottom = Math.max(bottom, container.bottom - panel.top + 12)
      }
    } else if ((panel.left + panel.right) / 2 < midX) {
      left = Math.max(left, panel.right - container.left + 12)
    } else {
      right = Math.max(right, container.right - panel.left + 12)
    }
  }
  // Keep a usable central strip on small screens. The panels can then be collapsed.
  const fit = (span: number, reserved: number) =>
    Math.min(1, Math.max(0, span - 100) / Math.max(1, reserved))
  const scaleX = fit(container.width, left + right)
  const scaleY = fit(container.height, top + bottom)
  return {
    top: Math.round(top * scaleY),
    bottom: Math.round(bottom * scaleY),
    left: Math.round(left * scaleX),
    right: Math.round(right * scaleX),
  }
}

export function usePrintInsets(
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
): PrintInsets {
  const [insets, setInsets] = useState(NO_PRINT_INSETS)
  useEffect(() => {
    const container = ref.current
    if (!enabled || !container) {
      setInsets(NO_PRINT_INSETS)
      return
    }
    const measure = () => {
      const next = printInsetsForPanels(
        container.getBoundingClientRect(),
        Array.from(
          container.querySelectorAll<HTMLElement>('[data-map-panel="true"]'),
        ).map((el) => el.getBoundingClientRect()),
      )
      setInsets((previous) =>
        previous.left === next.left &&
        previous.right === next.right &&
        previous.top === next.top &&
        previous.bottom === next.bottom
          ? previous
          : next,
      )
    }
    const resize = new ResizeObserver(measure)
    const observe = () => {
      resize.disconnect()
      resize.observe(container)
      container
        .querySelectorAll('[data-map-panel="true"]')
        .forEach((el) => resize.observe(el))
      measure()
    }
    const mutation = new MutationObserver(observe)
    mutation.observe(container, { childList: true, subtree: true })
    observe()
    window.addEventListener('resize', measure)
    return () => {
      resize.disconnect()
      mutation.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [ref, enabled])
  return enabled ? insets : NO_PRINT_INSETS
}
